import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import RemoveIcon from '@mui/icons-material/Remove';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';

import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import ReportsMobileBottomNav from 'Components/ReportsMobileBottomNav';

const getOverallProgress = (modules) => {
  let improvedCount = 0;
  let degradedCount = 0;
  (modules || []).forEach((mod) => {
    if (mod.changed) {
      if (mod.improved) improvedCount++;
      else degradedCount++;
    }
  });
  if (improvedCount > degradedCount) return 'up';
  if (degradedCount > improvedCount) return 'down';
  return 'neutral';
};

function SyncProgressReport(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [syncDates, setSyncDates] = React.useState([]);
  const [selectedDate1, setSelectedDate1] = React.useState('');
  const [selectedDate2, setSelectedDate2] = React.useState('');
  const [schoolFilter, setSchoolFilter] = React.useState('');
  const [schoolSearchText, setSchoolSearchText] = React.useState('');
  const [schoolOptions, setSchoolOptions] = React.useState([]);
  const [schoolOptionsLoading, setSchoolOptionsLoading] = React.useState(false);
  const [compareData, setCompareData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [datesLoading, setDatesLoading] = React.useState(false);
  const [coordinatorFilter, setCoordinatorFilter] = React.useState('');
  const [supervisorFilter, setSupervisorFilter] = React.useState('');
  const [coordinatorOptions, setCoordinatorOptions] = React.useState([]);
  const [supervisorOptions, setSupervisorOptions] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);

  // New Quick Table Filters: 'all' | 'improved' | 'degraded' | 'unchanged'
  const [progressFilter, setProgressFilter] = React.useState('all');
  const [selectedModuleFilter, setSelectedModuleFilter] = React.useState('');
  const [tableSearchText, setTableSearchText] = React.useState('');

  const tableSectionRef = React.useRef(null);
  const tableContainerRef = React.useRef(null);

  React.useEffect(() => {
    getRequest(GET_URL.client_track_report.api, { coordinator_options: 1 }, props).then((response) => {
      if (response?.status === 200) setCoordinatorOptions(response.data?.data || []);
    }).catch(() => {});
    getRequest(GET_URL.client_track_report.api, { supervisor_options: 1 }, props).then((response) => {
      if (response?.status === 200) setSupervisorOptions(response.data?.data || []);
    }).catch(() => {});
  }, [props]);

  React.useEffect(() => {
    setDatesLoading(true);
    getRequest(
      GET_URL.client_track_report.api,
      { sync_dates: 1 },
      props
    )
      .then((response) => {
        setDatesLoading(false);
        if (response?.status === 200) {
          const dates = response.data?.data || [];
          setSyncDates(dates);
          if (dates.length >= 2) {
            setSelectedDate1(dates[1].date);
            setSelectedDate2(dates[0].date);
          } else if (dates.length === 1) {
            setSelectedDate1(dates[0].date);
          }
        }
      })
      .catch(() => setDatesLoading(false));
  }, [props]);

  React.useEffect(() => {
    getRequest(
      GET_URL.client_track_report.api,
      { institute_options: 1, ...(schoolSearchText.trim() && { search: schoolSearchText.trim() }) },
      props
    ).then((response) => {
      if (response?.status === 200) {
        setSchoolOptions(response.data?.data || []);
      }
    }).catch(() => {});
  }, [props, schoolSearchText]);

  React.useEffect(() => {
    if (!selectedDate1 || !selectedDate2 || selectedDate1 === selectedDate2) {
      setCompareData(null);
      return;
    }
    setLoading(true);
    getRequest(
      GET_URL.client_track_report.api,
      {
        sync_compare: 1,
        sync_date1: selectedDate1,
        sync_date2: selectedDate2,
        ...(schoolFilter.trim() && { school: schoolFilter.trim() }),
        ...(coordinatorFilter && { coordinator: coordinatorFilter }),
        ...(supervisorFilter && { supervisor: supervisorFilter }),
      },
      props
    )
      .then((response) => {
        setLoading(false);
        if (response?.status === 200) {
          setCompareData(response.data?.data || null);
          setPage(0);
        }
      })
      .catch(() => setLoading(false));
  }, [selectedDate1, selectedDate2, schoolFilter, coordinatorFilter, supervisorFilter, props]);

  const summary = compareData?.summary || {};
  const rows = compareData?.rows || [];

  const date1Label = React.useMemo(() => {
    const entry = syncDates.find((d) => d.date === selectedDate1);
    return entry?.label || selectedDate1;
  }, [syncDates, selectedDate1]);

  const date2Label = React.useMemo(() => {
    const entry = syncDates.find((d) => d.date === selectedDate2);
    return entry?.label || selectedDate2;
  }, [syncDates, selectedDate2]);

  // Total improvements and degradations across all modules
  const { totalNetAdoptions, improvedModulesCount } = React.useMemo(() => {
    let net = 0;
    let improved = 0;
    Object.values(summary).forEach((data) => {
      const uChange = data.using_change || 0;
      net += uChange;
      if (uChange > 0) improved++;
    });
    return { totalNetAdoptions: net, improvedModulesCount: improved };
  }, [summary]);

  // Counts of schools by progress type
  const { improvedSchoolsCount, degradedSchoolsCount, unchangedSchoolsCount } = React.useMemo(() => {
    let imp = 0;
    let deg = 0;
    let unc = 0;
    rows.forEach((r) => {
      const p = getOverallProgress(r.modules);
      if (p === 'up') imp++;
      else if (p === 'down') deg++;
      else unc++;
    });
    return { improvedSchoolsCount: imp, degradedSchoolsCount: deg, unchangedSchoolsCount: unc };
  }, [rows]);

  // Filtered and Sorted Rows
  const filteredRows = React.useMemo(() => {
    return rows.filter((row) => {
      // 1. Text filter
      if (tableSearchText.trim()) {
        const term = tableSearchText.toLowerCase();
        if (!row.institute_name.toLowerCase().includes(term)) return false;
      }

      // 2. Progress status filter
      if (progressFilter !== 'all') {
        const prog = getOverallProgress(row.modules);
        if (progressFilter === 'improved' && prog !== 'up') return false;
        if (progressFilter === 'degraded' && prog !== 'down') return false;
        if (progressFilter === 'unchanged' && prog !== 'neutral') return false;
      }

      // 3. Module filter
      if (selectedModuleFilter) {
        const mod = (row.modules || []).find((m) => m.key === selectedModuleFilter);
        if (!mod || !mod.changed) return false;
      }

      return true;
    }).sort((a, b) => {
      const progA = getOverallProgress(a.modules);
      const progB = getOverallProgress(b.modules);
      const score = { up: 2, down: 1, neutral: 0 };
      if (score[progA] !== score[progB]) {
        return score[progB] - score[progA];
      }
      return a.institute_name.localeCompare(b.institute_name);
    });
  }, [rows, tableSearchText, progressFilter, selectedModuleFilter]);

  // Render a clean visual badge for each module cell
  const renderCellBadge = (mod) => {
    // 1. Started using between sync dates (Improved!)
    if (mod.changed && mod.improved) {
      return (
        <Tooltip title={`Started using between ${date1Label} and ${date2Label}`} arrow placement="top">
          <Chip
            size="small"
            icon={<ArrowUpwardIcon style={{ fontSize: 13, color: '#15803d' }} />}
            label="Started"
            sx={{
              bgcolor: '#dcfce7',
              color: '#15803d',
              fontWeight: 800,
              fontSize: '0.72rem',
              height: 22,
              border: '1px solid #86efac',
              cursor: 'help',
            }}
          />
        </Tooltip>
      );
    }

    // 2. Stopped using (Degraded)
    if (mod.changed && !mod.improved) {
      return (
        <Tooltip title={`Stopped using between ${date1Label} and ${date2Label}`} arrow placement="top">
          <Chip
            size="small"
            icon={<ArrowDownwardIcon style={{ fontSize: 13, color: '#b91c1c' }} />}
            label="Dropped"
            sx={{
              bgcolor: '#fee2e2',
              color: '#b91c1c',
              fontWeight: 800,
              fontSize: '0.72rem',
              height: 22,
              border: '1px solid #fca5a5',
              cursor: 'help',
            }}
          />
        </Tooltip>
      );
    }

    // 3. Actively using (No change)
    if (mod.status2 === 'using') {
      return (
        <Tooltip title="Actively using in both sync snapshots" arrow placement="top">
          <Chip
            size="small"
            label="Using"
            sx={{
              bgcolor: '#f0fdf4',
              color: '#166534',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 20,
              border: '1px solid #dcfce7',
            }}
          />
        </Tooltip>
      );
    }

    // 4. Never used in both (No change) -> Soft, polite muted dash instead of loud red text!
    return (
      <Tooltip title="Never used in both snapshots" arrow placement="top">
        <Typography
          variant="body2"
          sx={{ color: '#cbd5e1', fontWeight: 800, userSelect: 'none', cursor: 'default' }}
        >
          —
        </Typography>
      </Tooltip>
    );
  };

  return (
    <Box p={{ xs: 1.5, sm: 2.5 }} maxWidth={1440} mx="auto">
      {/* ============================================================== */}
      {/* 1. STICKY TOP FILTER & COMPARISON TOOLBAR                      */}
      {/* ============================================================== */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: { xs: 66, sm: 66 },
          zIndex: 90,
          p: { xs: 1.5, sm: 2 },
          mb: 2.5,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.08)',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} mb={1.5}>
          <Box display="flex" alignItems="center" gap={1}>
            <CompareArrowsIcon sx={{ fontSize: 28, color: '#2563eb' }} />
            <Box>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={800} color="#0f172a">
                Sync Progress Report
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Compare two sync snapshots to track which institutions started or dropped modules.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* Sync Dates Comparison Row */}
        <Grid container spacing={1.5} alignItems="center">
          {/* Older Date (From) */}
          <Grid item xs={12} sm={5} md={3.5}>
            <TextField
              select
              fullWidth
              size="small"
              label="From (Baseline Snapshot)"
              value={selectedDate1}
              onChange={(e) => setSelectedDate1(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">Select older sync date...</MenuItem>
              {syncDates.map((d) => (
                <MenuItem key={d.date} value={d.date} disabled={d.date === selectedDate2}>
                  {d.label} ({d.saved_count} records)
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Indicator Arrow */}
          <Grid item xs={12} sm={2} md={1} sx={{ textAlign: 'center' }}>
            <TrendingUpIcon sx={{ fontSize: 28, color: '#2563eb' }} />
          </Grid>

          {/* Newer Date (To) */}
          <Grid item xs={12} sm={5} md={3.5}>
            <TextField
              select
              fullWidth
              size="small"
              label="To (Latest Snapshot)"
              value={selectedDate2}
              onChange={(e) => setSelectedDate2(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">Select newer sync date...</MenuItem>
              {syncDates.map((d) => (
                <MenuItem key={d.date} value={d.date} disabled={d.date === selectedDate1}>
                  {d.label} ({d.saved_count} records)
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Coordinator */}
          <Grid item xs={6} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Coordinator"
              value={coordinatorFilter}
              onChange={(e) => { setCoordinatorFilter(e.target.value); setPage(0); }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">All Coordinators</MenuItem>
              {coordinatorOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>{opt.name || opt.id}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Supervisor */}
          <Grid item xs={6} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Supervisor"
              value={supervisorFilter}
              onChange={(e) => { setSupervisorFilter(e.target.value); setPage(0); }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">All Supervisors</MenuItem>
              {supervisorOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>{opt.name || opt.id}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {selectedDate1 && selectedDate2 && selectedDate1 === selectedDate2 && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Please select two different sync dates to compare.
          </Typography>
        )}
      </Paper>

      {(loading || datesLoading) && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {compareData && (
        <>
          {/* ============================================================== */}
          {/* 2. EXECUTIVE PROGRESS BANNER (REPLACES LONELY USERS CARD)      */}
          {/* ============================================================== */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2.5,
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
            }}
          >
            <Grid container spacing={2} alignItems="center">
              {/* Total Users */}
              <Grid item xs={6} sm={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PeopleAltOutlinedIcon sx={{ color: '#2563eb', fontSize: 24 }} />
                  <Box>
                    <Typography variant="caption" fontWeight={700} color="#64748b" textTransform="uppercase" fontSize="0.7rem">
                      Total Active Users
                    </Typography>
                    <Typography variant="h5" fontWeight={900} color="#0f172a">
                      {Number(compareData.total_users || 0).toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Total Institutes */}
              <Grid item xs={6} sm={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <SchoolOutlinedIcon sx={{ color: '#0d9488', fontSize: 24 }} />
                  <Box>
                    <Typography variant="caption" fontWeight={700} color="#64748b" textTransform="uppercase" fontSize="0.7rem">
                      Institutes Compared
                    </Typography>
                    <Typography variant="h5" fontWeight={900} color="#0f172a">
                      {compareData.total_institutes || 0}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Net Adoption Shift */}
              <Grid item xs={6} sm={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TrendingUpIcon sx={{ color: totalNetAdoptions >= 0 ? '#16a34a' : '#dc2626', fontSize: 24 }} />
                  <Box>
                    <Typography variant="caption" fontWeight={700} color="#64748b" textTransform="uppercase" fontSize="0.7rem">
                      Net Modules Adopted
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight={900}
                      color={totalNetAdoptions >= 0 ? '#15803d' : '#b91c1c'}
                    >
                      {totalNetAdoptions > 0 ? `+${totalNetAdoptions}` : totalNetAdoptions}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Comparison Window */}
              <Grid item xs={6} sm={3}>
                <Box>
                  <Typography variant="caption" fontWeight={700} color="#64748b" textTransform="uppercase" fontSize="0.7rem">
                    Comparison Window
                  </Typography>
                  <Typography variant="body2" fontWeight={700} color="#334155" noWrap>
                    {date1Label} ➔ {date2Label}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* ============================================================== */}
          {/* 3. MODULE-WISE ADOPTION CHANGE CARDS (CLICKABLE FILTERS)        */}
          {/* ============================================================== */}
          <Box mb={2.5}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Box>
                <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                  Module Adoption Shifts
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Click any module card to filter the table below to institutions that changed that module
                </Typography>
              </Box>

              {selectedModuleFilter && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setSelectedModuleFilter('')}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}
                >
                  Clear Module Filter ✕
                </Button>
              )}
            </Box>

            <Grid container spacing={1.5}>
              {Object.entries(summary).map(([key, data]) => {
                const usingChange = data.using_change || 0;
                const isSelected = selectedModuleFilter === key;
                const isPositive = usingChange > 0;
                const isNegative = usingChange < 0;

                return (
                  <Grid item xs={12} sm={6} md={3} key={key}>
                    <Card
                      elevation={0}
                      onClick={() => setSelectedModuleFilter(isSelected ? '' : key)}
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        border: '2px solid',
                        borderColor: isSelected
                          ? '#2563eb'
                          : isPositive
                          ? '#bbf7d0'
                          : isNegative
                          ? '#fecaca'
                          : '#e2e8f0',
                        backgroundColor: isSelected
                          ? '#eff6ff'
                          : isPositive
                          ? '#f0fdf4'
                          : '#ffffff',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        },
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.8}>
                        <Typography variant="subtitle2" fontWeight={800} color="#0f172a" noWrap>
                          {data.label}
                        </Typography>

                        {isPositive ? (
                          <Chip
                            size="small"
                            icon={<ArrowUpwardIcon style={{ fontSize: 12, color: '#15803d' }} />}
                            label={`+${usingChange}`}
                            sx={{
                              height: 20,
                              fontWeight: 800,
                              fontSize: '0.7rem',
                              bgcolor: '#dcfce7',
                              color: '#15803d',
                            }}
                          />
                        ) : isNegative ? (
                          <Chip
                            size="small"
                            icon={<ArrowDownwardIcon style={{ fontSize: 12, color: '#b91c1c' }} />}
                            label={`${usingChange}`}
                            sx={{
                              height: 20,
                              fontWeight: 800,
                              fontSize: '0.7rem',
                              bgcolor: '#fee2e2',
                              color: '#b91c1c',
                            }}
                          />
                        ) : (
                          <Chip
                            size="small"
                            label="Steady"
                            sx={{ height: 20, fontWeight: 600, fontSize: '0.68rem', bgcolor: '#f1f5f9' }}
                          />
                        )}
                      </Box>

                      <Box display="flex" justifyContent="space-between" alignItems="baseline">
                        <Typography variant="caption" color="text.secondary">
                          Now Using: <strong>{data.using_tab2}</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Never Used: <strong>{data.never_used_tab2}</strong>
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* ============================================================== */}
          {/* 4. CLEAN TABLE WITH INSTANT FILTER TABS & CLEAR BADGES         */}
          {/* ============================================================== */}
          <Paper
            ref={tableSectionRef}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              backgroundColor: '#ffffff',
              overflow: 'hidden',
            }}
          >
            {/* Table Action Bar with Quick Progress Tabs */}
            <Box
              sx={{
                p: 1.5,
                borderBottom: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1.5,
              }}
            >
              {/* Quick Status Segment Buttons */}
              <Box display="flex" alignItems="center" gap={0.8} flexWrap="wrap">
                {[
                  { key: 'all', label: 'All Schools', count: rows.length },
                  { key: 'improved', label: '🟢 Started Features', count: improvedSchoolsCount, color: '#16a34a' },
                  { key: 'degraded', label: '🔴 Dropped Features', count: degradedSchoolsCount, color: '#dc2626' },
                  { key: 'unchanged', label: '⚪ No Change', count: unchangedSchoolsCount },
                ].map((tab) => (
                  <Button
                    key={tab.key}
                    size="small"
                    variant={progressFilter === tab.key ? 'contained' : 'outlined'}
                    onClick={() => { setProgressFilter(tab.key); setPage(0); }}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2,
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      py: 0.3,
                      px: 1.2,
                      ...(progressFilter === tab.key
                        ? {
                            backgroundColor: tab.color || '#2563eb',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: tab.color || '#1d4ed8' },
                          }
                        : {
                            color: '#475569',
                            borderColor: '#cbd5e1',
                          }),
                    }}
                  >
                    {tab.label} ({tab.count})
                  </Button>
                ))}
              </Box>

              {/* Table Quick Search */}
              <Box sx={{ width: { xs: '100%', sm: 260 } }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search in table..."
                  value={tableSearchText}
                  onChange={(e) => { setTableSearchText(e.target.value); setPage(0); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                    endAdornment: tableSearchText ? (
                      <IconButton size="small" onClick={() => setTableSearchText('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    ) : null,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#ffffff' } }}
                />
              </Box>
            </Box>

            {/* Table */}
            <TableContainer ref={tableContainerRef} sx={{ maxHeight: 650 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', minWidth: 240, borderBottom: '1px solid #e2e8f0' }}>
                      Institution Name
                    </TableCell>
                    {Object.values(summary).map((s) => (
                      <TableCell
                        key={s.label}
                        sx={{
                          fontWeight: 800,
                          bgcolor: '#f8fafc',
                          textAlign: 'center',
                          minWidth: 110,
                          fontSize: '0.75rem',
                          borderBottom: '1px solid #e2e8f0',
                        }}
                      >
                        {s.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={Object.keys(summary).length + 1} align="center" sx={{ py: 8 }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Comparing progress...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={Object.keys(summary).length + 1} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          No institutions match this filter.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.slice(page * pageSize, page * pageSize + pageSize).map((row) => {
                      const prog = getOverallProgress(row.modules);
                      return (
                        <TableRow
                          key={row.institute_name}
                          hover
                          sx={{
                            backgroundColor:
                              prog === 'up'
                                ? 'rgba(240, 253, 244, 0.4)'
                                : prog === 'down'
                                ? 'rgba(254, 242, 242, 0.4)'
                                : 'inherit',
                          }}
                        >
                          {/* Institution with Progress Icon */}
                          <TableCell sx={{ fontWeight: 700, py: 1.2 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              {prog === 'up' && (
                                <Tooltip title="Overall Progress: Improved" arrow>
                                  <ArrowUpwardIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                                </Tooltip>
                              )}
                              {prog === 'down' && (
                                <Tooltip title="Overall Progress: Degraded" arrow>
                                  <ArrowDownwardIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                                </Tooltip>
                              )}
                              {prog === 'neutral' && (
                                <TrendingFlatIcon sx={{ fontSize: 16, color: '#cbd5e1' }} />
                              )}
                              <Typography variant="body2" fontWeight={700} color="#0f172a">
                                {row.institute_name}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* 8 Module Status Badges */}
                          {row.modules.map((mod) => (
                            <TableCell key={mod.key} align="center" sx={{ py: 1 }}>
                              {renderCellBadge(mod)}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Footer */}
            {filteredRows.length > 0 && (
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={page}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[10, 25, 50, 100]}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setPageSize(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{ borderTop: '1px solid #f1f5f9' }}
              />
            )}
          </Paper>
        </>
      )}

      {!compareData && !loading && selectedDate1 && selectedDate2 && selectedDate1 !== selectedDate2 && (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <Typography variant="body1" color="text.secondary">
            No comparison data available for the selected dates.
          </Typography>
        </Paper>
      )}

      {(!selectedDate1 || !selectedDate2) && !datesLoading && (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <CompareArrowsIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            Select two sync dates above to compare adoption progress.
          </Typography>
        </Paper>
      )}

      {/* Mobile Bottom Navigation */}
      <ReportsMobileBottomNav />
    </Box>
  );
}

export default SyncProgressReport;
